// filenameGenerator.ts - Utilitários de Geração de Nome de Arquivo
// Bigwa v2.1 - Janeiro 2026
// Versão compatível com namingConfigTypes.ts v2.0

import {
  NamingCategory,
  NamingValues,
  GlobalSettings,
  CategoryConfig
} from '../types/namingConfigTypes';

// ═══════════════════════════════════════════════════════════════════════════════
// FUNÇÕES DE SANITIZAÇÃO
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Remove acentos de uma string
 */
export const removeAccents = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

/**
 * Sanitiza string para uso em nome de arquivo
 */
export const sanitizeForFilename = (value: string, separator: string = '_'): string => {
  return removeAccents(value)
    .replace(/[^a-zA-Z0-9]/g, separator)
    .replace(new RegExp(`${separator}+`, 'g'), separator)
    .replace(new RegExp(`^${separator}|${separator}$`, 'g'), '');
};

/**
 * Limpa nome de arquivo genérico
 */
export const cleanupGenericFilename = (filename: string): string => {
  return filename
    .replace(/^IMG_|^DSC_|^DCIM_|^Screenshot_?/i, '')
    .replace(/\d{8}_\d{6}/, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
};

// ═══════════════════════════════════════════════════════════════════════════════
// FUNÇÕES DE FORMATAÇÃO
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Formata número com padding
 */
export const formatSequentialNumber = (num: number, padding: number = 3): string => {
  return String(num).padStart(padding, '0');
};

/**
 * Formata data para nome de arquivo
 */
export const formatDateForFilename = (dateStr: string, format: string = 'YYYY-MM-DD'): string => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  switch (format) {
    case 'DD-MM-YYYY':
      return `${day}-${month}-${year}`;
    case 'YYYYMMDD':
      return `${year}${month}${day}`;
    case 'YYYY-MM-DD':
    default:
      return `${year}-${month}-${day}`;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// FUNÇÕES DE EXTENSÃO
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Obtém extensão do arquivo
 */
export const getFileExtension = (filename: string): string => {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
};

/**
 * Remove extensão do arquivo
 */
export const removeFileExtension = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.substring(0, lastDot) : filename;
};

// ═══════════════════════════════════════════════════════════════════════════════
// FUNÇÕES DE ABREVIAÇÃO
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Abrevia título para nome de arquivo
 */
export const abbreviateTitle = (title: string, maxLength: number = 30): string => {
  const cleaned = removeAccents(title)
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .trim();
  
  if (cleaned.length <= maxLength) return cleaned.replace(/\s+/g, '_');
  
  const words = cleaned.split(/\s+/);
  let result = '';
  
  for (const word of words) {
    if ((result + word).length > maxLength) break;
    result += (result ? '_' : '') + word;
  }
  
  return result || cleaned.substring(0, maxLength);
};

/**
 * Abrevia nome de periódico
 */
export const abbreviateJournal = (journal: string): string => {
  const stopWords = ['the', 'of', 'and', 'for', 'in', 'on', 'to', 'a', 'an'];
  return journal
    .split(/\s+/)
    .filter(w => !stopWords.includes(w.toLowerCase()))
    .map(w => w.charAt(0).toUpperCase())
    .join('');
};

/**
 * Abrevia nome de instituição
 */
export const abbreviateInstitution = (institution: string): string => {
  const stopWords = ['university', 'of', 'the', 'and', 'instituto', 'universidade', 'de', 'da', 'do'];
  return institution
    .split(/\s+/)
    .filter(w => !stopWords.includes(w.toLowerCase()))
    .map(w => w.charAt(0).toUpperCase())
    .join('');
};

// ═══════════════════════════════════════════════════════════════════════════════
// FUNÇÕES DE EXTRAÇÃO
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Extrai data de nome de arquivo
 */
export const extractDateFromFilename = (filename: string): string | null => {
  // Padrão YYYY-MM-DD
  const isoMatch = filename.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return isoMatch[0];
  
  // Padrão YYYYMMDD
  const compactMatch = filename.match(/(\d{4})(\d{2})(\d{2})/);
  if (compactMatch) {
    return `${compactMatch[1]}-${compactMatch[2]}-${compactMatch[3]}`;
  }
  
  return null;
};

// ═══════════════════════════════════════════════════════════════════════════════
// FUNÇÕES DE UNICIDADE
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Garante nome único adicionando sufixo numérico se necessário
 */
export const ensureUniqueName = (
  baseName: string,
  existingNames: string[],
  extension: string
): string => {
  const fullName = `${baseName}.${extension}`;
  if (!existingNames.includes(fullName)) return fullName;
  
  let counter = 1;
  let uniqueName = `${baseName}_${counter}.${extension}`;
  
  while (existingNames.includes(uniqueName)) {
    counter++;
    uniqueName = `${baseName}_${counter}.${extension}`;
  }
  
  return uniqueName;
};

// ═══════════════════════════════════════════════════════════════════════════════
// FUNÇÕES DE TEMPLATE (Legado - mantidas para compatibilidade)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Substitui variáveis em template
 */
export const replaceTemplateVariables = (
  template: string,
  variables: Record<string, string>
): string => {
  let result = template;
  for (const [key, value] of Object.entries(variables)) {
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
  }
  return result;
};

/**
 * Valida template
 */
export const validateTemplate = (template: string): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!template || template.trim().length === 0) {
    errors.push('Template não pode ser vazio');
  }
  
  // Verificar variáveis não fechadas
  const openBraces = (template.match(/\{/g) || []).length;
  const closeBraces = (template.match(/\}/g) || []).length;
  
  if (openBraces !== closeBraces) {
    errors.push('Template tem variáveis mal formadas');
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// GERAÇÃO DE NOME (Nova implementação)
// ═══════════════════════════════════════════════════════════════════════════════

export interface GenerateNameOptions {
  category: NamingCategory;
  values: NamingValues;
  originalFilename: string;
  config: CategoryConfig;
  globalSettings: GlobalSettings;
  nextNumber: number;
}

/**
 * Gera nome de arquivo baseado na configuração
 */
export const generateFilename = (options: GenerateNameOptions): string => {
  const { values, originalFilename, config, globalSettings, nextNumber } = options;
  const { separator, dateFormat, numberPadding } = globalSettings;
  
  const ext = getFileExtension(originalFilename);
  const parts: string[] = [];
  
  // 1. Prefixo
  if (config.prefix) {
    parts.push(config.prefix);
  }
  
  // 2. Base (número ou data)
  if (config.basePattern === 'numero') {
    parts.push(formatSequentialNumber(nextNumber, numberPadding));
  } else {
    const dateValue = values.data || new Date().toISOString().split('T')[0];
    parts.push(formatDateForFilename(dateValue, dateFormat));
  }
  
  // 3. Toggles ativos (na ordem definida)
  const activeToggles = config.toggles
    .filter(t => t.enabled)
    .sort((a, b) => a.order - b.order);
  
  for (const toggle of activeToggles) {
    // Não adicionar data novamente se já é a base
    if (toggle.id === 'data' && config.basePattern === 'data') continue;
    
    const value = values[toggle.id] || toggle.fixedValue;
    if (value) {
      parts.push(sanitizeForFilename(value, separator));
    }
  }
  
  return parts.join(separator) + '.' + ext;
};

/**
 * Gera preview do nome (sem incrementar contador)
 */
export const previewFilename = (options: GenerateNameOptions): string => {
  return generateFilename(options);
};

// ═══════════════════════════════════════════════════════════════════════════════
// EXPORTS DEFAULT
// ═══════════════════════════════════════════════════════════════════════════════

export default {
  removeAccents,
  sanitizeForFilename,
  cleanupGenericFilename,
  formatSequentialNumber,
  formatDateForFilename,
  getFileExtension,
  removeFileExtension,
  abbreviateTitle,
  abbreviateJournal,
  abbreviateInstitution,
  extractDateFromFilename,
  ensureUniqueName,
  replaceTemplateVariables,
  validateTemplate,
  generateFilename,
  previewFilename,
};
